import { useNavigate } from "react-router-dom";
import { useResolvedImageUrl } from "../../hooks/useResolvedImageUrl";
import { useClientTranslations } from "../../lib/i18n/client";
import type { ResumeItem } from "../../services/educationService";
import styles from "./ResumeCarousel.module.css";

interface ResumeCarouselProps {
	items: ResumeItem[];
}

/** Seconds → "m:ss" (e.g. 204 → "3:24"). */
function formatClock(seconds: number): string {
	const s = Math.max(0, Math.round(seconds));
	const m = Math.floor(s / 60);
	return `${m}:${String(s % 60).padStart(2, "0")}`;
}

// 12px media glyph (Figma label row). aria-hidden — the text label carries meaning.
function TypeIcon({ type }: { type: ResumeItem["contentType"] }) {
	return (
		<svg
			className={styles.icon}
			viewBox="0 0 12 12"
			fill="none"
			aria-hidden="true"
		>
			{type === "podcast" ? (
				<>
					<rect
						x="4.5"
						y="1"
						width="3"
						height="6"
						rx="1.5"
						fill="currentColor"
					/>
					<path
						d="M3 5.5a3 3 0 0 0 6 0M6 8.5V11"
						stroke="currentColor"
						strokeWidth="1"
						strokeLinecap="round"
					/>
				</>
			) : (
				<path d="M3 2.5v7l6-3.5-6-3.5Z" fill="currentColor" />
			)}
		</svg>
	);
}

function ResumeCard({
	item,
	onOpen,
}: {
	item: ResumeItem;
	onOpen: () => void;
}) {
	const { t } = useClientTranslations("education");
	// Resolve the auth-gated proxy image to a bearer-fetched blob (presigned
	// passes through); gradient placeholder shows until/unless it loads.
	const { url: imageUrl } = useResolvedImageUrl(item.imageUrl);

	// Elapsed-of-total label, e.g. "3:24 of 10:00". Real `durationSeconds` when
	// present; otherwise derive the total from progress. If neither, show just
	// the elapsed time.
	const total =
		item.durationSeconds ??
		(item.progress > 0 ? item.currentTime / item.progress : 0);
	const timeText =
		total > 0
			? t("resume.progressLabel", {
					current: formatClock(item.currentTime),
					total: formatClock(total),
				})
			: formatClock(item.currentTime);

	return (
		<button type="button" className={styles.card} onClick={onOpen}>
			<div
				className={styles.thumbnail}
				data-testid="resume-thumb"
				aria-hidden="true"
				style={
					imageUrl
						? {
								backgroundImage: `url(${imageUrl})`,
								backgroundSize: "cover",
								backgroundPosition: "center",
							}
						: undefined
				}
			/>
			<div className={styles.info}>
				<div className={styles.top}>
					<TypeIcon type={item.contentType} />
					<span className={styles.label}>
						{t(`contentTypes.${item.contentType}`)}
					</span>
				</div>
				<h3 className={styles.title}>{item.title}</h3>
				<div className={styles.graph}>
					<div className={styles.progressTrack}>
						<div
							className={styles.progressFill}
							data-testid="progress-fill"
							style={{ width: `${Math.min(1, item.progress) * 100}%` }}
						/>
					</div>
					<span className={styles.time}>{timeText}</span>
				</div>
			</div>
		</button>
	);
}

export function ResumeCarousel({ items }: ResumeCarouselProps) {
	const navigate = useNavigate();

	return (
		<div className={styles.list}>
			{items.map((item) => (
				<ResumeCard
					key={item.id}
					item={item}
					onOpen={() =>
						navigate(
							`/education/${item.id}/${item.contentType === "video" ? "video" : "podcast"}`,
							{ state: { resumeAt: item.currentTime } },
						)
					}
				/>
			))}
		</div>
	);
}
